import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Info,
  Loader2,
  XCircle,
} from "lucide-react";
import {
  useListPreviewClients,
  usePreviewTemplateForClient,
} from "@workspace/api-client-react";
import type {
  ClientPreviewProfile,
  ClientProfileId,
  RenderedClientPreview,
} from "@workspace/api-client-react";
import { rewriteLinksForPreview } from "@/lib/previewHtml";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type MultiClientPreviewProps = {
  templateId: number;
  variables: Record<string, string>;
  subjectOverride?: string;
  displaySubject?: string;
  missingVariables?: string[];
  className?: string;
  // Failure of the parent's shell render call (subject + missing-vars).
  // When set, an inline error strip is shown in the preview chrome with a
  // retry button that calls `onRetryShell`. Pass `null` for the healthy
  // state.
  shellError?: string | null;
  onRetryShell?: () => void;
};

const SEVERITY_STYLES: Record<
  "info" | "warning" | "error",
  { icon: typeof Info; color: string; tint: string }
> = {
  info: {
    icon: Info,
    color: "text-sky-700 dark:text-sky-300",
    tint: "bg-sky-500/10 border-sky-500/20",
  },
  warning: {
    icon: AlertTriangle,
    color: "text-amber-700 dark:text-amber-300",
    tint: "bg-amber-500/10 border-amber-500/20",
  },
  error: {
    icon: XCircle,
    color: "text-red-700 dark:text-red-300",
    tint: "bg-red-500/10 border-red-500/20",
  },
};

function cacheKey(
  profile: ClientProfileId,
  variables: Record<string, string>,
  subjectOverride: string | undefined,
): string {
  const sortedVars = Object.keys(variables)
    .sort()
    .map((k) => `${k}=${variables[k] ?? ""}`)
    .join("|");
  return `${profile}::${subjectOverride ?? ""}::${sortedVars}`;
}

export function MultiClientPreview({
  templateId,
  variables,
  subjectOverride,
  displaySubject,
  missingVariables,
  className,
  shellError,
  onRetryShell,
}: MultiClientPreviewProps) {
  const profilesQuery = useListPreviewClients();
  const profiles: ClientPreviewProfile[] = useMemo(
    () => profilesQuery.data ?? [],
    [profilesQuery.data],
  );

  const [activeProfile, setActiveProfile] = useState<ClientProfileId | null>(
    null,
  );
  useEffect(() => {
    if (!activeProfile && profiles.length > 0) {
      setActiveProfile(profiles[0].id);
    }
  }, [profiles, activeProfile]);

  // Per-profile cache so re-clicking a tab is instant after first load.
  const cacheRef = useRef<Map<string, RenderedClientPreview>>(new Map());
  const previewMutation = usePreviewTemplateForClient();
  // Track in-flight key so a stale fetch can't overwrite a fresher one.
  const inFlightKeyRef = useRef<string | null>(null);

  const [results, setResults] = useState<
    Record<ClientProfileId, RenderedClientPreview | undefined>
  >({} as Record<ClientProfileId, RenderedClientPreview | undefined>);
  const [errors, setErrors] = useState<
    Record<ClientProfileId, string | undefined>
  >({} as Record<ClientProfileId, string | undefined>);
  const [retryNonce, setRetryNonce] = useState(0);
  const [issuesOpen, setIssuesOpen] = useState(true);

  // Stable hash of the inputs so we reset the cache when they change.
  const inputsKey = useMemo(
    () => cacheKey("gmail-web", variables, subjectOverride),
    [variables, subjectOverride],
  );

  useEffect(() => {
    cacheRef.current = new Map();
    setResults({} as Record<ClientProfileId, RenderedClientPreview | undefined>);
    setErrors({} as Record<ClientProfileId, string | undefined>);
  }, [inputsKey, templateId]);

  // Fetch the active profile lazily; `retryNonce` re-triggers on retry.
  useEffect(() => {
    if (!activeProfile || !templateId) return;
    const key = cacheKey(activeProfile, variables, subjectOverride);
    const cached = cacheRef.current.get(key);
    if (cached) {
      setResults((prev) => ({ ...prev, [activeProfile]: cached }));
      setErrors((prev) => ({ ...prev, [activeProfile]: undefined }));
      return;
    }
    let cancelled = false;
    inFlightKeyRef.current = key;
    setErrors((prev) => ({ ...prev, [activeProfile]: undefined }));
    previewMutation
      .mutateAsync({
        id: templateId,
        data: {
          variables,
          subjectOverride:
            subjectOverride !== undefined ? subjectOverride : undefined,
          clientProfile: activeProfile,
        },
      })
      .then((res) => {
        if (cancelled) return;
        if (inFlightKeyRef.current !== key) return;
        cacheRef.current.set(key, res);
        setResults((prev) => ({ ...prev, [activeProfile]: res }));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (inFlightKeyRef.current !== key) return;
        const message =
          err instanceof Error
            ? err.message
            : "Couldn't render this client preview.";
        setErrors((prev) => ({ ...prev, [activeProfile]: message }));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfile, templateId, inputsKey, retryNonce]);

  const activeProfileMeta = profiles.find((p) => p.id === activeProfile);
  const activeResult = activeProfile ? results[activeProfile] : undefined;
  const activeError = activeProfile ? errors[activeProfile] : undefined;
  const isLoadingActive = !activeResult && !activeError;
  const issues = activeResult?.compatibilityIssues ?? [];
  const subject =
    displaySubject ?? activeResult?.subject ?? "";

  return (
    <div
      className={cn(
        "w-full bg-[#f4f4f5] flex flex-col relative overflow-hidden",
        className,
      )}
      data-testid="multi-client-preview"
    >
      <div className="px-3 py-2 border-b border-border/50 bg-white/60 flex flex-col gap-2 shrink-0">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">
            Live Preview
          </span>
          {missingVariables && missingVariables.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-amber-600 font-medium">
              <AlertTriangle className="w-3.5 h-3.5" />
              Missing: {missingVariables.join(", ")}
            </div>
          )}
        </div>
        {shellError ? (
          <div
            className="flex items-start justify-between gap-2 px-2 py-1.5 rounded bg-red-500/10 border border-red-500/20"
            data-testid="preview-shell-error"
          >
            <div className="flex items-start gap-1.5 min-w-0">
              <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-red-700 dark:text-red-300" />
              <span
                className="text-xs text-red-700 dark:text-red-300 break-words"
                data-testid="preview-shell-error-message"
              >
                Couldn't render preview — {shellError}
              </span>
            </div>
            {onRetryShell && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 px-2 text-xs shrink-0"
                onClick={onRetryShell}
                data-testid="preview-shell-error-retry"
              >
                Retry
              </Button>
            )}
          </div>
        ) : null}
        {profiles.length > 0 && activeProfile ? (
          <Tabs
            value={activeProfile}
            onValueChange={(v) => setActiveProfile(v as ClientProfileId)}
          >
            <TabsList className="h-8 w-full justify-start overflow-x-auto bg-muted/70">
              {profiles.map((p) => (
                <TabsTrigger
                  key={p.id}
                  value={p.id}
                  className="text-xs h-7 px-3 shrink-0"
                  data-testid={`preview-tab-${p.id}`}
                  title={p.description}
                >
                  {p.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        ) : profilesQuery.isError ? (
          <div
            className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-amber-500/10 border border-amber-500/20"
            data-testid="preview-catalogue-error"
          >
            <span className="text-xs text-amber-700 dark:text-amber-300">
              Couldn't load preview clients.
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => profilesQuery.refetch()}
              data-testid="preview-catalogue-retry"
            >
              Retry
            </Button>
          </div>
        ) : null}
      </div>

      <div className="flex-1 p-3 sm:p-6 overflow-auto flex items-start justify-center">
        <div
          className="bg-white shadow-xl rounded-md overflow-hidden flex flex-col border border-border/10 h-full max-h-[800px]"
          style={{
            width: "100%",
            maxWidth: activeProfileMeta?.viewportWidth
              ? `${activeProfileMeta.viewportWidth}px`
              : "600px",
          }}
          data-testid="preview-frame-wrapper"
        >
          <div className="border-b border-gray-100 px-4 py-3 bg-white shrink-0">
            <div className="text-sm">
              <span className="text-gray-500 w-16 inline-block">Subject:</span>{" "}
              <span className="font-medium text-gray-900">
                {subject || "—"}
              </span>
            </div>
          </div>
          {activeResult ? (
            <iframe
              srcDoc={rewriteLinksForPreview(activeResult.html)}
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              className="w-full flex-1 bg-white border-0"
              title={`Email preview — ${activeProfileMeta?.label ?? ""}`}
              data-testid={`preview-iframe-${activeProfile}`}
              data-viewport-width={activeProfileMeta?.viewportWidth}
            />
          ) : activeProfile && errors[activeProfile] ? (
            <div
              className="flex-1 flex flex-col items-center justify-center gap-3 text-sm text-muted-foreground p-6 text-center"
              data-testid={`preview-error-${activeProfile}`}
            >
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <div className="max-w-xs">
                <div className="font-medium text-foreground mb-1">
                  Preview failed
                </div>
                <div className="text-xs text-muted-foreground break-words">
                  {errors[activeProfile]}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setRetryNonce((n) => n + 1)}
                data-testid={`preview-retry-${activeProfile}`}
              >
                Try again
              </Button>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Rendering preview…
            </div>
          )}
        </div>
      </div>

      {/* Collapsible compatibility-issues panel. */}
      <div className="border-t border-border/50 bg-white/70 shrink-0 max-h-[40%] flex flex-col">
        <button
          type="button"
          onClick={() => setIssuesOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-2 text-xs font-medium hover:bg-muted/30 transition-colors"
          data-testid="preview-issues-toggle"
          aria-expanded={issuesOpen}
        >
          <span className="flex items-center gap-2">
            <span className="uppercase tracking-widest text-muted-foreground">
              Compatibility issues
            </span>
            {isLoadingActive ? (
              <span
                className="text-[11px] text-muted-foreground font-medium"
                data-testid="preview-issues-loading"
              >
                Loading…
              </span>
            ) : issues.length > 0 ? (
              <Badge
                variant="secondary"
                className="text-[10px] h-4 px-1.5 leading-tight"
              >
                {issues.length}
              </Badge>
            ) : (
              <span className="text-[11px] text-emerald-600 font-medium">
                All good
              </span>
            )}
          </span>
          {issuesOpen ? (
            <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
          )}
        </button>
        {issuesOpen && (
          <div
            className="px-4 pb-3 overflow-auto"
            data-testid="preview-issues-list"
          >
            {activeProfileMeta && (
              <div className="mb-2">
                <p className="text-[11px] text-muted-foreground leading-snug">
                  {activeProfileMeta.description}
                </p>
                {activeProfileMeta.quirks?.length > 0 && (
                  <details
                    className="mt-1"
                    data-testid="preview-profile-quirks"
                  >
                    <summary className="text-[11px] text-muted-foreground cursor-pointer hover:text-foreground select-none">
                      Simulated quirks ({activeProfileMeta.quirks.length})
                    </summary>
                    <ul className="mt-1 ml-3 list-disc text-[11px] text-muted-foreground space-y-0.5 leading-snug">
                      {activeProfileMeta.quirks.map((q, i) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
            {isLoadingActive ? (
              <p className="text-xs text-muted-foreground">
                Checking {activeProfileMeta?.label ?? "client"} compatibility…
              </p>
            ) : issues.length === 0 ? (
              <p className="text-xs text-emerald-700">
                No known {activeProfileMeta?.label ?? "client"} compatibility
                issues detected in this template.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {issues.map((issue, i) => {
                  const sev = SEVERITY_STYLES[issue.severity];
                  const Icon = sev.icon;
                  return (
                    <li
                      key={`${issue.rule}-${i}`}
                      className={cn(
                        "flex items-start gap-2 text-xs rounded border px-2.5 py-1.5",
                        sev.tint,
                      )}
                      data-testid={`preview-issue-${issue.rule}`}
                    >
                      <Icon
                        className={cn("w-3.5 h-3.5 mt-0.5 shrink-0", sev.color)}
                      />
                      <span className="text-foreground/90 leading-snug">
                        {issue.message}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
